// NEW FORM BUILDER COMPONENT

import * as pdf from 'react-pdf';
import dayjs, { Dayjs } from 'dayjs';
import React, {
  memo,
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
} from 'react';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Box,
  Card,
  TextField,
  CardHeader,
  IconButton,
  Typography,
  CardContent,
  SelectChangeEvent,
} from '@mui/material';

import useLocales from 'src/hooks/useLocales';
import useFormData from 'src/hooks/useFormData';
import useResponsive from 'src/hooks/useResponsive';
import usePdfHandling from 'src/hooks/usePdfHandling';

import { useSelector } from 'src/redux/store';
import { AddressBookType } from 'src/@types/addressBook/types';

import useAuth from '../../../hooks/useAuth';
import DateElement from '../elements/DateElement';
import CheckboxElement from '../elements/CheckboxElement';
import UserDataElement from '../elements/UserDataElement';
import DropdownElement from '../elements/DropdownElement';
import DateRangeElement from '../elements/DateRangeElement';
import HyperlinkElement from '../elements/HyperlinkElement';
import SignatureElement from '../elements/SignatureElement';
import { fetchProfile } from '../../../redux/slices/profile';
import InputFieldElement from '../elements/InputFieldElement';
import StaticTextElement from '../elements/StaticTextElement';
import CustomListElement from '../elements/CustomListElement';
import LoadingScreen from '../../../components/LoadingScreen';
import RadioButtonElement from '../elements/RadioButtonElement';
import AddressBookElement from '../elements/AddressBookElement';
import { handleTextAlign } from '../../../utils/forms/formBuilderUtils';
import { UserDataType } from '../../../@types/forms/enums/UserDataType';
import { FormElementType } from '../../../@types/forms/enums/FormElementType';
import { AddressBookFilter } from '../../../@types/addressBook/addressBookFilter';
import { MAX_DOCUMENT_NAME_LENGTH, MIN_DOCUMENT_NAME_LENGTH } from '../constants/constants';
import {
  FormContent,
  Collaborator,
  ElementProps,
  PdfOffsetProps,
  DocumentPageSize,
} from '../../../@types/forms/types';
import {
  getAddressBookUsers,
  getAddressBookExternalUsers,
  getAddressBookInternalUsers,
} from '../../../redux/slices/addressBook';

const { Page, Document } = pdf;

// -----------------------------------------------------------------

type FormFillerProps = {
  formId: number | null;
  onContentChange: (content: FormContent[]) => void;
  collaborators: Collaborator[];
  setCollaborators: React.Dispatch<React.SetStateAction<Collaborator[]>>;
  isDocument: boolean;
  documentName: string;
  setDocumentName: (name: string) => void;
  fieldErrors: Record<string, { message: string; page: number; label: string }>;
  focusFieldId: string | null;
  onFocusFieldHandled: () => void;
  externalContent?: FormContent[];
  isCombinedMode?: boolean;
  /** Controls whether this FormFiller instance can respond to resize events */
  canResize?: boolean;
  /** Callback to notify parent of PDF MediaBox offset */
  onPdfOffsetChange?: (offset: PdfOffsetProps) => void;
};

export type GroupedUsersProps = {
  firstLetter: string;
  name: string;
};

const FormFiller: React.FC<FormFillerProps> = ({
  formId,
  onContentChange,
  collaborators,
  setCollaborators,
  isDocument,
  documentName,
  setDocumentName,
  fieldErrors,
  focusFieldId,
  onFocusFieldHandled,
  externalContent,
  isCombinedMode = false,
  canResize = true,
  onPdfOffsetChange,
}) => {
  const { t } = useLocales();
  const { user } = useAuth();
  const { form, file } = useSelector((state) => state.formBuilder);
  const { fetchTime, profile } = useSelector((state) => state.profile);
  const [isLoading, setIsLoading] = useState(false);
  const [layerSize, setLayerSize] = useState<DocumentPageSize | null>(null);
  const elementLayer = useRef<HTMLElement | null>(null);
  const elementRefs = useRef<Record<string, HTMLElement | null>>({});

  // Document name validation
  const isDocumentNameValid =
    documentName.length >= MIN_DOCUMENT_NAME_LENGTH &&
    documentName.length <= MAX_DOCUMENT_NAME_LENGTH;

  const {
    pageNumber,
    setPageNumber,
    numPages,
    onDocumentLoadSuccess,
    formViewport,
    pageNumberScale,
    setPageNumberScale,
    pdfOffset,
  } = usePdfHandling({ layerSize, shouldEnableResize: canResize });
  const { elements } = useFormData(formId, setIsLoading);
  const [internalContent, setInternalContent] = useState<FormContent[]>([]);
  const { users, externalUsers, internalUsers } = useSelector((state) => state.addressBook);

  // Use ref to always have the latest externalContent without stale closure
  const externalContentRef = useRef(externalContent);
  externalContentRef.current = externalContent;

  // Use external content if provided, otherwise use internal state
  const content = externalContent ?? internalContent;

  // Track if content has been initialized to skip initial useEffect call
  const isContentInitialized = useRef(false);
  const pendingContentUpdate = useRef<FormContent[] | null>(null);

  const setContent = useCallback((newContent: FormContent[] | ((prev: FormContent[]) => FormContent[])) => {
    setInternalContent((prevInternal) => {
      // Use ref to get the latest externalContent value
      const prevContent = externalContentRef.current ?? prevInternal;
      const updatedContent = typeof newContent === 'function' ? newContent(prevContent) : newContent;
      // Keep ref in sync so subsequent batched updates see the latest value
      externalContentRef.current = updatedContent;
      // Store pending update to notify parent after state update
      pendingContentUpdate.current = updatedContent;
      isContentInitialized.current = true;
      return updatedContent;
    });
  }, []);

  // Notify parent of content changes after state update (not during render)
  useEffect(() => {
    if (isContentInitialized.current && pendingContentUpdate.current !== null) {
      const pendingLength = pendingContentUpdate.current.length;
      const externalLength = externalContent?.length ?? 0;

      // Don't overwrite parent's content if we have fewer items
      // This prevents race conditions when document loads and updates parent,
      // but FormFiller's pending update still has old/fewer data
      if (pendingLength < externalLength) {
        pendingContentUpdate.current = null;
        return;
      }

      onContentChange(pendingContentUpdate.current);
      pendingContentUpdate.current = null;
    }
  }, [internalContent, onContentChange, externalContent]);

  const [groupedUsers, setGroupedUsers] = useState<GroupedUsersProps[]>([]);
  const [groupedInternalUsers, setGroupedInternalUsers] = useState<GroupedUsersProps[]>([]);
  const [groupedExternalUsers, setGroupedExternalUsers] = useState<GroupedUsersProps[]>([]);
  const [, setKey] = useState(0);
  const isMobile = useResponsive('down', 'sm');

  const handlePdfNavButtonClick = (i: number) => {
    setPageNumber(pageNumber + i);
  };

  // Create a Map for O(1) element lookups instead of O(n) array searches
  const contentMap = useMemo(() => {
    const map = new Map<string, { value: string[]; index: number }>();
    content.forEach((item, index) => {
      map.set(item.id, { value: item.value, index });
    });
    return map;
  }, [content]);

  const getElementContentValue = useCallback((id: string) => contentMap.get(id)?.value, [contentMap]);

  const groupUsers = (users, setGroupedUsersState) => {
    const newGroupedUsers = users.items.map((u) => {
      let firstLetter: string;

      if (u.lastName.length > 0) {
        firstLetter = u.lastName[0].toUpperCase();
      } else if (u.firstName.length > 0) {
        firstLetter = u.firstName[0].toUpperCase();
      } else {
        firstLetter = '*';
      }

      return {
        firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
        name: `${u.lastName} ${u.firstName}`,
      };
    });

    setGroupedUsersState(newGroupedUsers);
  };

  const handleInputFieldChangeContent = useCallback((ids: string[], value: string) => {
    setContent((prevContent) => {
      const newContent = [...prevContent];

      ids.forEach((id) => {
        const contentItem = { id, value: [value] };
        const elementIndex = newContent.findIndex((item) => item.id === id);

        if (elementIndex === -1) {
          newContent.push(contentItem);
        } else {
          newContent.splice(elementIndex, 1, contentItem);
        }
      });

      return newContent;
    });
  }, [setContent]);

  const handleInputFieldChange = useCallback((
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
    el: ElementProps
  ) => {
    const { value } = event.target;
    const { id } = el;

    const ids: string[] = [];

    ids.push(id);

    if (el.childElements && el.childElements.length > 0) {
      el.childElements.forEach((e) => {
        ids.push(e.id.toString());
      });
    }

    handleInputFieldChangeContent(ids, value);
  }, [handleInputFieldChangeContent]);

  const handleDropdownChange = useCallback((value: string, el: ElementProps) => {
    const { id } = el;
    const contentItem = { id, value: [value] };

    setContent((prevContent) => {
      const elementIndex = prevContent.findIndex((item) => item.id === id);

      if (elementIndex === -1) {
        return [...prevContent, contentItem];
      }
      const newContent = [...prevContent];
      newContent.splice(elementIndex, 1, contentItem);
      return newContent;
    });
  }, [setContent]);

  const handleAddressBookChange = useCallback((value: GroupedUsersProps[], el: ElementProps) => {
    const { id } = el;
    const newValue = value.map((u) => u.name);
    const contentItem = { id, value: newValue };

    setContent((prevContent) => {
      const elementIndex = prevContent.findIndex((item) => item.id === id);

      if (elementIndex === -1) {
        return [...prevContent, contentItem];
      }
      const newContent = [...prevContent];
      newContent.splice(elementIndex, 1, contentItem);
      return newContent;
    });
  }, [setContent]);

  const handleCheckboxChange = useCallback((el: ElementProps, index: number) => {
    const { id, formGroupOptions } = el;

    if (!formGroupOptions || index >= formGroupOptions.length) return;

    const optionLabel = formGroupOptions[index].label;

    setContent((prevContent) => {
      const elementIndex = prevContent.findIndex((item) => item.id === id);
      const currentValue = elementIndex !== -1 ? prevContent[elementIndex].value : [];

      // Toggle the option in the array
      const newValue = currentValue.includes(optionLabel)
        ? currentValue.filter((v) => v !== optionLabel)
        : [...currentValue, optionLabel];

      const contentItem = { id, value: newValue };

      if (elementIndex === -1) {
        return [...prevContent, contentItem];
      }
      const newContent = [...prevContent];
      newContent.splice(elementIndex, 1, contentItem);
      return newContent;
    });
  }, [setContent]);

  const handleRadioButtonChange = useCallback((el: ElementProps, index: number) => {
    const { id, formGroupOptions } = el;

    if (!formGroupOptions || index >= formGroupOptions.length) return;

    const optionLabel = formGroupOptions[index].label;
    const contentItem = { id, value: [optionLabel] };

    setContent((prevContent) => {
      const elementIndex = prevContent.findIndex((item) => item.id === id);

      if (elementIndex === -1) {
        return [...prevContent, contentItem];
      }
      const newContent = [...prevContent];
      newContent.splice(elementIndex, 1, contentItem);
      return newContent;
    });
  }, [setContent]);

  const handleDateChangeContent = useCallback((ids: string[], value: string) => {
    setContent((prevContent) => {
      const newContent = [...prevContent];

      ids.forEach((id) => {
        const contentItem = { id, value: [value] };
        const elementIndex = newContent.findIndex((item) => item.id === id);

        if (elementIndex === -1) {
          newContent.push(contentItem);
        } else {
          newContent.splice(elementIndex, 1, contentItem);
        }
      });

      return newContent;
    });
  }, [setContent]);

  const handleDateChange = useCallback((el: ElementProps, date: Dayjs | null) => {
    const value = date ? date.format('YYYY-MM-DD') : ''; // <-- buvusį defaultToday pakeičiam į ''
    const { id } = el;

    const ids: string[] = [id];
    if (el.childElements && el.childElements.length > 0) {
      el.childElements.forEach((e) => ids.push(e.id.toString()));
    }
    handleDateChangeContent(ids, value);
  }, [handleDateChangeContent]);

  const handleDateRangeChangeContent = useCallback((ids: string[], value: string[]) => {
    setContent((prevContent) => {
      const newContent = [...prevContent];

      ids.forEach((id) => {
        const contentItem = { id, value };
        const elementIndex = newContent.findIndex((item) => item.id === id);

        if (elementIndex === -1) {
          newContent.push(contentItem);
        } else {
          newContent.splice(elementIndex, 1, contentItem);
        }
      });

      return newContent;
    });
  }, [setContent]);

  const handleDateRangeChange = useCallback((el: ElementProps, date: [Dayjs | null, Dayjs | null]) => {
    const value = [
      date[0] ? date[0].format('YYYY-MM-DD') : '',
      date[1] ? date[1].format('YYYY-MM-DD') : '',
    ];
    const { id } = el;

    const ids: string[] = [];

    ids.push(id);

    if (el.childElements && el.childElements.length > 0) {
      el.childElements.forEach((e) => {
        ids.push(e.id.toString());
      });
    }

    handleDateRangeChangeContent(ids, value);
  }, [handleDateRangeChangeContent]);

  const contentRef = useRef<FormContent[]>([]);

  // Sync contentRef with actual content (externalContent or internalContent)
  // This ensures contentRef always has the latest data including data from other collaborators
  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  // Initialize all UserDataElement content when profile loads (for all pages, not just current)
  useEffect(() => {
    if (!profile || !elements || elements.length === 0) return;

    const userDataElements = elements.filter((el) => el.type === FormElementType.User);
    if (userDataElements.length === 0) return;

    // Build a map of UserData element updates
    const userDataUpdates: { id: string; value: string[] }[] = [];

    userDataElements.forEach((el) => {
      const { id, userDataOption } = el;
      let label = '';

      switch (userDataOption) {
        case UserDataType.Name:
          label = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim();
          break;
        case UserDataType.Email:
          label = user?.email || profile?.email || '';
          break;
        case UserDataType.Phone:
          label = profile?.phoneNumber || '';
          break;
        case UserDataType.Company:
          label = profile?.companyName || '';
          break;
        case UserDataType.Job:
          label = profile?.jobTitles?.map((job) => job.name).join(', ') || '';
          break;
        case UserDataType.Address:
          label = [profile?.address, profile?.city, profile?.zipCode, profile?.country]
            .filter(Boolean)
            .join(', ');
          break;
        case UserDataType.ManagersName:
          label = `${profile?.userDoclogixManager?.firstName ?? ''} ${profile?.userDoclogixManager?.lastName ?? ''}`.trim();
          break;
        case UserDataType.ManagersEmail:
          label = profile?.userDoclogixManager?.email || '';
          break;
        default:
          label = '';
      }

      if (label) {
        userDataUpdates.push({ id, value: [label] });
      }
    });

    if (userDataUpdates.length === 0) return;

    // Use functional update to merge UserData values with existing content
    setContent((prevContent) => {
      let hasChanges = false;
      const newContent = [...prevContent];

      userDataUpdates.forEach(({ id, value }) => {
        const elementIndex = newContent.findIndex((e) => e.id === id);
        if (elementIndex === -1) {
          newContent.push({ id, value });
          hasChanges = true;
        } else if (newContent[elementIndex].value[0] !== value[0]) {
          newContent.splice(elementIndex, 1, { id, value });
          hasChanges = true;
        }
      });

      return hasChanges ? newContent : prevContent;
    });
  }, [profile, elements, user, setContent]);

  const handleSignatureChange = useCallback((el: ElementProps, imgString: string) => {
    const { id } = el;
    const contentItem = { id, value: [imgString] };

    setContent((prevContent) => {
      const elementIndex = prevContent.findIndex((item) => item.id === id);

      if (elementIndex === -1) {
        return [...prevContent, contentItem];
      }
      const newContent = [...prevContent];
      newContent.splice(elementIndex, 1, contentItem);
      return newContent;
    });
  }, [setContent]);

  useEffect(() => {
    if (elementLayer.current) {
      setLayerSize({
        width: elementLayer.current.offsetWidth,
        height: elementLayer.current.offsetHeight,
      });
    }

    if (formViewport !== null && formViewport[pageNumber - 1]) {
      const pageNumberScale = formViewport[pageNumber - 1].scale;
      setPageNumberScale(pageNumberScale);
    }
  }, [pageNumber, formViewport]);

  // Notify parent of PDF offset changes
  useEffect(() => {
    if (onPdfOffsetChange && (pdfOffset.xOffset !== 0 || pdfOffset.yOffset !== 0)) {
      onPdfOffsetChange(pdfOffset);
    }
  }, [pdfOffset, onPdfOffsetChange]);

  // Handle window resize to recalculate element positions
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const handleResize = () => {
      // Check canResize directly - effect will re-run when it changes
      if (!canResize || !elementLayer.current) return;

      // Debounce resize events to prevent flickering
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        if (elementLayer.current && canResize) {
          setLayerSize({
            width: elementLayer.current.offsetWidth,
            height: elementLayer.current.offsetHeight,
          });
        }
      }, 150);
    };

    // Initial size calculation when canResize becomes true
    if (canResize && elementLayer.current) {
      setLayerSize({
        width: elementLayer.current.offsetWidth,
        height: elementLayer.current.offsetHeight,
      });
    }

    // Only set up listeners if resize is enabled
    if (!canResize) return;

    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver !== 'undefined' && elementLayer.current) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(elementLayer.current);
    } else {
      // Fallback to window resize event
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [file, numPages, canResize]);

  useEffect(() => {
    const usersFilter = new AddressBookFilter();
    // all users
    getAddressBookUsers(usersFilter).then();

    if (!fetchTime || Date.now() - fetchTime > 1000) {
      fetchProfile().then();
    }
  }, []);

  useEffect(() => {
    const addressBookEls = elements.filter((el) => el.type === FormElementType.AddressBook);

    const isExternal = addressBookEls.some((el) => el.isForExternal === true);
    const isInternal = addressBookEls.some((el) => el.isForInternal === true);

    if (isExternal) {
      const usersFilter = new AddressBookFilter();
      usersFilter.RoleFilterQuery = AddressBookType.ExternalUser;
      getAddressBookExternalUsers(usersFilter).then();
    }

    if (isInternal) {
      const usersFilter = new AddressBookFilter();
      usersFilter.RoleFilterQuery = AddressBookType.InternalUser;
      getAddressBookInternalUsers(usersFilter).then();
    }
  }, [elements]);

  useEffect(() => {
    if (users) {
      groupUsers(users, setGroupedUsers);
    }
  }, [users]);

  useEffect(() => {
    if (externalUsers) {
      groupUsers(externalUsers, setGroupedExternalUsers);
    }
  }, [externalUsers]);

  useEffect(() => {
    if (internalUsers) {
      groupUsers(internalUsers, setGroupedInternalUsers);
    }
  }, [internalUsers]);

  useEffect(() => {
    const formCollaborators = form?.collaborators ?? [];

    // Update collaborators when form changes
    if (formCollaborators.length > 0) {
      // Check if collaborators have actually changed to avoid unnecessary updates
      const hasChanged =
        formCollaborators.length !== collaborators.length ||
        formCollaborators.some(
          (fc, index) => fc.collaboratorId !== collaborators[index]?.collaboratorId
        );

      if (hasChanged) {
        setCollaborators(formCollaborators);
      }
    }
  }, [form?.collaborators, collaborators, setCollaborators]);

  // Helper function to check if element should be disabled based on collaborator email
  const isElementDisabled = (el: ElementProps): boolean => {
    // Check if current user is an owner - owners can edit all fields
    const userEmail = user?.email?.toLowerCase();
    const isCurrentUserOwner = collaborators.some(
      (c) => c.role === 'owner' && c.email?.toLowerCase() === userEmail
    );

    if (isCurrentUserOwner) {
      return false; // Owner can edit all fields
    }

    if (!el.collaboratorId) {
      return false; // No collaborator assigned, element is enabled for everyone
    }

    const collaborator = collaborators.find((c) => c.collaboratorId === el.collaboratorId);

    if (!collaborator || !collaborator.email) {
      return false; // No collaborator found or no email, keep enabled
    }

    const collaboratorEmail = collaborator.email?.toLowerCase();
    const isCurrentUserCollaborator = collaboratorEmail === userEmail;

    // Collaborator can only fill if emails match AND canFill is true
    if (isCurrentUserCollaborator) {
      return !collaborator.canFill; // Disable if canFill is false
    }

    return true; // Disable for other users (emails don't match)
  };

  useEffect(() => {
    if (form && form.name) {
      const d = new Date();

      setDocumentName(`${form.name}_${d.toLocaleString('lt').replaceAll(' ', '_')}`);
    }
  }, [form]);

  // Handle navigation to field when required field is clicked
  useEffect(() => {
    if (!focusFieldId || !elements || elements.length === 0) return;

    // Find the element with the matching ID
    const targetElement = elements.find((el) => el.id === focusFieldId);
    if (!targetElement) {
      onFocusFieldHandled();
      return;
    }

    // Navigate to the page if needed
    if (targetElement.page !== pageNumber) {
      setPageNumber(targetElement.page || 1);
    }

    // Focus the element after a short delay to ensure rendering
    const timeoutId = setTimeout(() => {
      const elementRef = elementRefs.current[focusFieldId];
      if (elementRef) {
        // Scroll into view
        elementRef.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Find the actual input/textarea/select element inside the Box
        const inputElement = elementRef.querySelector('input, textarea, select') as
          | HTMLInputElement
          | HTMLTextAreaElement
          | HTMLSelectElement
          | null;

        if (inputElement) {
          // Focus the input element
          inputElement.focus();

          // If it's an input or textarea, select the content
          if (
            inputElement instanceof HTMLInputElement ||
            inputElement instanceof HTMLTextAreaElement
          ) {
            inputElement.select();
          }
        }
      }
      onFocusFieldHandled();
    }, 300); // Delay to ensure page has changed and element is rendered

    // Cleanup function to clear timeout if effect re-runs or component unmounts
    return () => clearTimeout(timeoutId);
  }, [focusFieldId, elements, pageNumber, setPageNumber, onFocusFieldHandled]);

  return (
    <Box sx={{ p: 0, m: 0 }}>
      {!isCombinedMode && (
        <CardHeader
          sx={{ pt: 0, mb: 2 }}
          title={
            isDocument ? (
              <Typography variant="h4">{documentName}</Typography>
            ) : (
              <TextField
                label={t('forms.documentName')}
                fullWidth
                id="new-document-name"
                value={documentName}
                size="small"
                variant="standard"
                error={!isDocumentNameValid && documentName.length > 0}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setDocumentName(event.target.value.trim());
                }}
                helperText={
                  documentName.length === 0
                    ? t('forms.validationErrorMessage.fieldRequired')
                    : documentName.length < MIN_DOCUMENT_NAME_LENGTH
                      ? t('forms.validationErrorMessage.atLeastCharacters', {
                          number: MIN_DOCUMENT_NAME_LENGTH,
                        })
                      : documentName.length > MAX_DOCUMENT_NAME_LENGTH
                        ? t('forms.validationErrorMessage.maximumCharacters', {
                            number: MAX_DOCUMENT_NAME_LENGTH,
                          })
                        : `${documentName.length}/${MAX_DOCUMENT_NAME_LENGTH} ${t('forms.characters')}`
                }
                slotProps={{
                  formHelperText: {
                    sx: {
                      color:
                        documentName.length === 0 || !isDocumentNameValid
                          ? 'error.main'
                          : 'success.main',
                    },
                  },
                }}
              />
            )
          }
        />
      )}

      <Card>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center', mb: 1 }}
        >
          <IconButton
            aria-label="arrow-back"
            size="large"
            disabled={pageNumber === 1}
            onClick={() => handlePdfNavButtonClick(-1)}
            id="adbor6fl"
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          <Typography>
            {t('forms.documentNavigation.page')} {pageNumber} {t('forms.documentNavigation.of')}{' '}
            {numPages}
          </Typography>

          <IconButton
            aria-label="arrow-forward"
            size="large"
            disabled={pageNumber === numPages}
            onClick={() => handlePdfNavButtonClick(1)}
            id="6et13v24"
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
        <CardContent sx={{ maxHeight: '100%', p: 0 }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              overflow: isMobile && pageNumberScale !== 0.5 ? 'auto' : 'hidden',
            }}
          >
            {/* PDF DOCUMENT */}
            <Box sx={{ width: '100%' }}>
              <Document
                file={file}
                onLoadSuccess={onDocumentLoadSuccess}
                externalLinkTarget="_blank"
              >
                <Page
                  pageNumber={pageNumber}
                  width={layerSize !== null ? layerSize.width : undefined}
                  renderTextLayer={false}
                />
              </Document>
            </Box>
            {/* LAYER FOR ELEMENTS */}
            <Box
              ref={elementLayer}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 1,
                zIndex: 1000,
                boxSizing: 'border-box',
                // overflow: 'auto',
              }}
            >
              {elements &&
                elements.length > 0 &&
                formViewport &&
                elements
                  .filter((element) => element.page === pageNumber)
                  .map((el, i) => {
                    const scaledDimensions = {
                      x: el.x * pageNumberScale,
                      y: el.y * pageNumberScale,
                      width: el.width * pageNumberScale,
                      height: el.height * pageNumberScale,
                      fontSize: el.fontSize * pageNumberScale,
                    };

                    // ELEMENT => StaticText
                    if (el.type === FormElementType.StaticText) {
                      return (
                        <Box
                          key={el.id}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                          }}
                        >
                          <StaticTextElement
                            el={el}
                            pageNumberScale={pageNumberScale}
                            isMobileView={false}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => InputField
                    if (el.type === FormElementType.InputField) {
                      const elementContentValue = getElementContentValue(el.id);
                      const collaborator = el.collaboratorId
                        ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                        : undefined;

                      const isDisabled = isElementDisabled(el);

                      return (
                        <Box
                          key={el.id}
                          ref={(ref: HTMLDivElement | null) => {
                            elementRefs.current[el.id] = ref;
                          }}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                          onBlur={() => (el.isParent ? setKey((k) => k + 1) : null)}
                        >
                          <InputFieldElement
                            isChild={el.isChild}
                            el={el}
                            scaledDimensions={scaledDimensions}
                            handleTextAlign={handleTextAlign}
                            handleInputFieldChange={handleInputFieldChange}
                            elementContentValue={elementContentValue}
                            pageNumberScale={pageNumberScale}
                            collaboratorColor={collaborator?.color}
                            disabled={isDisabled}
                            errorMessage={fieldErrors[el.id]?.message}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => Dropdown
                    if (el.type === FormElementType.Dropdown) {
                      const elementContentValue = getElementContentValue(el.id);
                      const collaborator = el.collaboratorId
                        ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                        : undefined;
                      const isDisabled = isElementDisabled(el);

                      return (
                        <Box
                          key={el.id}
                          ref={(ref: HTMLDivElement | null) => {
                            elementRefs.current[el.id] = ref;
                          }}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <DropdownElement
                            el={el}
                            scaledDimensions={scaledDimensions}
                            handleTextAlign={handleTextAlign}
                            handleDropdownChange={handleDropdownChange}
                            elementContentValue={elementContentValue}
                            pageNumberScale={pageNumberScale}
                            collaboratorColor={collaborator?.color}
                            disabled={isDisabled}
                            errorMessage={fieldErrors[el.id]?.message}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => Checkbox
                    if (el.type === FormElementType.Checkbox) {
                      const elementContentValue = getElementContentValue(el.id);
                      const collaborator = el.collaboratorId
                        ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                        : undefined;
                      const isDisabled = isElementDisabled(el);

                      return (
                        <Box
                          key={el.id}
                          ref={(ref: HTMLDivElement | null) => {
                            elementRefs.current[el.id] = ref;
                          }}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <CheckboxElement
                            el={el}
                            pageNumberScale={pageNumberScale}
                            elementContentValue={elementContentValue}
                            handleCheckboxChange={handleCheckboxChange}
                            isFormFillerPage
                            collaboratorColor={collaborator?.color}
                            disabled={isDisabled}
                            errorMessage={fieldErrors[el.id]?.message}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => RadioButton
                    if (el.type === FormElementType.RadioButton) {
                      const elementContentValue = getElementContentValue(el.id);
                      const collaborator = el.collaboratorId
                        ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                        : undefined;
                      const isDisabled = isElementDisabled(el);

                      return (
                        <Box
                          key={el.id}
                          ref={(ref: HTMLDivElement | null) => {
                            elementRefs.current[el.id] = ref;
                          }}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <RadioButtonElement
                            el={el}
                            pageNumberScale={pageNumberScale}
                            elementContentValue={elementContentValue}
                            handleRadioButtonChange={handleRadioButtonChange}
                            isFormFillerPage
                            collaboratorColor={collaborator?.color}
                            disabled={isDisabled}
                            errorMessage={fieldErrors[el.id]?.message}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => Date
                    if (el.type === FormElementType.Date) {
                      const elementContentValue = getElementContentValue(el.id);
                      const collaborator = el.collaboratorId
                        ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                        : undefined;
                      const isDisabled = isElementDisabled(el);

                      return (
                        <Box
                          key={el.id}
                          ref={(ref: HTMLDivElement | null) => {
                            elementRefs.current[el.id] = ref;
                          }}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <DateElement
                            el={el}
                            pageNumberScale={pageNumberScale}
                            isFormFillerPage
                            elementContentValueString={elementContentValue?.[0]}
                            handleDateChange={handleDateChange}
                            collaboratorColor={collaborator?.color}
                            disabled={isDisabled}
                            errorMessage={fieldErrors[el.id]?.message}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => DateRange
                    if (el.type === FormElementType.DateRange) {
                      const elementContentValue = getElementContentValue(el.id);
                      const elementContentValueAsDayjs: [Dayjs | null, Dayjs | null] = [null, null];

                      if (elementContentValue) {
                        for (let i = 0; i < elementContentValue.length; i++) {
                          if (elementContentValue[i] !== null) {
                            elementContentValueAsDayjs[i] = dayjs(elementContentValue[i]);
                          }
                        }
                      }

                      const collaborator = el.collaboratorId
                        ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                        : undefined;
                      const isDisabled = isElementDisabled(el);

                      return (
                        <Box
                          key={el.id}
                          ref={(ref: HTMLDivElement | null) => {
                            elementRefs.current[el.id] = ref;
                          }}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <DateRangeElement
                            el={el}
                            pageNumberScale={pageNumberScale}
                            isFormFillerPage
                            elementContentValue={elementContentValueAsDayjs}
                            handleDateRangeChange={handleDateRangeChange}
                            collaboratorColor={collaborator?.color}
                            disabled={isDisabled}
                            errorMessage={fieldErrors[el.id]?.message}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => Image

                    if (el.type === FormElementType.Image) {
                      return (
                        <Box
                          key={el.id}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: '100%',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                          }}
                        >
                          <img
                            src={`${el.file}`}
                            alt={`${el.label}`}
                            width={el.width * pageNumberScale}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => CustomList
                    if (el.type === FormElementType.CustomList) {
                      const elementContentValue = getElementContentValue(el.id);
                      const collaborator = el.collaboratorId
                        ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                        : undefined;
                      const isDisabled = isElementDisabled(el);

                      return (
                        <Box
                          key={el.id}
                          ref={(ref: HTMLDivElement | null) => {
                            elementRefs.current[el.id] = ref;
                          }}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <CustomListElement
                            el={el}
                            scaledDimensions={scaledDimensions}
                            handleTextAlign={handleTextAlign}
                            elementContentValue={elementContentValue}
                            handleDropdownChange={handleDropdownChange}
                            pageNumberScale={pageNumberScale}
                            collaboratorColor={collaborator?.color}
                            disabled={isDisabled}
                            errorMessage={fieldErrors[el.id]?.message}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => AddressBook
                    if (el.type === FormElementType.AddressBook) {
                      const elementContentValue = getElementContentValue(el.id);
                      let usersByType = groupedUsers;

                      if (el.isForExternal) {
                        usersByType = groupedExternalUsers;
                      } else if (el.isForInternal) {
                        usersByType = groupedInternalUsers;
                      }

                      const collaborator = el.collaboratorId
                        ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                        : undefined;
                      const isDisabled = isElementDisabled(el);

                      return (
                        <Box
                          key={el.id}
                          ref={(ref: HTMLDivElement | null) => {
                            elementRefs.current[el.id] = ref;
                          }}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <AddressBookElement
                            el={el}
                            groupedUsers={usersByType}
                            elementContentValue={elementContentValue}
                            handleAddressBookChange={handleAddressBookChange}
                            pageNumberScale={pageNumberScale}
                            collaboratorColor={collaborator?.color}
                            disabled={isDisabled}
                            errorMessage={fieldErrors[el.id]?.message}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => Hyperlink
                    if (el.type === FormElementType.Hyperlink) {
                      return (
                        <Box
                          key={el.id}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                          }}
                        >
                          <HyperlinkElement el={el} pageNumberScale={pageNumberScale} />
                        </Box>
                      );
                    }

                    // ELEMENT => Signature
                    if (el.type === FormElementType.Signature) {
                      const size = {
                        width: scaledDimensions.width,
                        height: scaledDimensions.height,
                      };
                      const elementConetentValue = getElementContentValue(el.id);
                      const collaborator = el.collaboratorId
                        ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                        : undefined;
                      const isDisabled = isElementDisabled(el);

                      return (
                        <Box
                          key={el.id}
                          ref={(ref: HTMLDivElement | null) => {
                            elementRefs.current[el.id] = ref;
                          }}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            height: scaledDimensions.height,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <SignatureElement
                            el={el}
                            pageNumberScale={pageNumberScale}
                            isFormFillerPage
                            elementConetentValue={elementConetentValue}
                            handleSignatureChange={handleSignatureChange}
                            size={size}
                            collaboratorColor={collaborator?.color}
                            disabled={isDisabled}
                            errorMessage={fieldErrors[el.id]?.message}
                          />
                        </Box>
                      );
                    }

                    // ELEMENT => User
                    if (el.type === FormElementType.User) {
                      return (
                        <Box
                          key={el.id}
                          sx={{
                            position: 'absolute',
                            top: scaledDimensions.y,
                            left: scaledDimensions.x,
                            width: scaledDimensions.width,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'flex-end',
                          }}
                        >
                          <UserDataElement
                            el={el}
                            pageNumberScale={pageNumberScale}
                            isFormFillerPage
                          />
                        </Box>
                      );
                    }
                  })}
            </Box>
          </Box>
        </CardContent>
        {/* PDF NAVIGATION */}
        <Box
          sx={{ display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center', mt: 1 }}
        >
          <IconButton
            aria-label="arrow-back"
            size="large"
            disabled={pageNumber === 1}
            onClick={() => handlePdfNavButtonClick(-1)}
            id="1jgfik5k"
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          <Typography>
            {t('forms.documentNavigation.page')} {pageNumber} {t('forms.documentNavigation.of')}{' '}
            {numPages}
          </Typography>

          <IconButton
            aria-label="arrow-forward"
            size="large"
            disabled={pageNumber === numPages}
            onClick={() => handlePdfNavButtonClick(1)}
            id="nyyzobuk"
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
      </Card>
      {isLoading && <LoadingScreen />}
    </Box>
  );
};

/**
 * Custom comparison function for React.memo
 * Only re-render if critical props have changed
 */
const arePropsEqual = (prevProps: FormFillerProps, nextProps: FormFillerProps): boolean => {
  // Always re-render if formId changes
  if (prevProps.formId !== nextProps.formId) return false;

  // Always re-render if mode changes
  if (prevProps.isCombinedMode !== nextProps.isCombinedMode) return false;
  if (prevProps.canResize !== nextProps.canResize) return false;

  // Always re-render if document name changes
  if (prevProps.documentName !== nextProps.documentName) return false;

  // Always re-render if isDocument flag changes
  if (prevProps.isDocument !== nextProps.isDocument) return false;

  // Always re-render if focusFieldId changes (to trigger focus)
  if (prevProps.focusFieldId !== nextProps.focusFieldId) return false;

  // Compare externalContent - check both length and actual values
  if (prevProps.externalContent?.length !== nextProps.externalContent?.length) return false;
  if (prevProps.externalContent !== nextProps.externalContent) return false;

  // Compare collaborators length (shallow comparison)
  if (prevProps.collaborators.length !== nextProps.collaborators.length) return false;

  // Compare fieldErrors keys (shallow comparison)
  const prevErrorKeys = Object.keys(prevProps.fieldErrors);
  const nextErrorKeys = Object.keys(nextProps.fieldErrors);
  if (prevErrorKeys.length !== nextErrorKeys.length) return false;

  // If all checks pass, props are equal - skip re-render
  return true;
};

export default memo(FormFiller, arePropsEqual);
